import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import OpenInNewWindow from "@DSG/Icons/OpenInNewWindow";
import colorPallete, {
    bodyFonts,
    buttonFonts,
    headlineFonts,
} from "@DSG/MobiusOverrides/styles/variables";
import { displayHeading } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img/Img";
import { GridContainer, GridItem, Link } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});
type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;
// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    dsgPlusIcon = "dsgPlusIcon",
    dsgPlusIconAltText = "dsgPlusIconAltText",
    dsgPlusTitle = "dsgPlusTitle",
    dsgProgressToGoalTitle = "dsgProgressToGoalTitle",
    dsgGoalBetweenRangeMessage = "dsgGoalBetweenRangeMessage",
    dsgZeroGoalMessage = "dsgZeroGoalMessage",
    dsgGoalExceedsMessage = "dsgGoalExceedsMessage",
    dsgHMIErrorMessage = "dsgHMIErrorMessage",
    dsgSignUpMessage = "dsgSignUpMessage",
    viewDsgPlusSiteLinkText = "viewDsgPlusSiteLinkText",
    viewDsgPlusSiteLinkUrl = "viewDsgPlusSiteLinkUrl",
    openViewLinkInNewWindow = "openViewLinkInNewWindow",
    signUpDsgLinkText = "signUpDsgLinkText",
    signUpDsgLinkUrl = "signUpDsgLinkUrl",
    openSignUpLinkInNewWindow = "openSignUpLinkInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.dsgPlusIcon]: string;
        [fields.dsgPlusIconAltText]: string;
        [fields.dsgPlusTitle]: string;
        [fields.dsgProgressToGoalTitle]: string;
        [fields.dsgGoalBetweenRangeMessage]: string;
        [fields.dsgZeroGoalMessage]: string;
        [fields.dsgGoalExceedsMessage]: string;
        [fields.dsgHMIErrorMessage]: string;
        [fields.dsgSignUpMessage]: string;
        [fields.viewDsgPlusSiteLinkText]: string;
        [fields.viewDsgPlusSiteLinkUrl]: LinkFieldValue;
        [fields.openViewLinkInNewWindow]: boolean;
        [fields.signUpDsgLinkText]: string;
        [fields.signUpDsgLinkUrl]: LinkFieldValue;
        [fields.openSignUpLinkInNewWindow]: boolean;
    };
}

export interface DsgRewardsInformationStyles {
    container?: InjectableCss;
    dsgPlusGridContainer?: GridContainerProps;
    linkGridItem?: GridItemProps;
    dsgRewardsProgressGridItem?: GridItemProps;
    dsgRewardsInfoGridItem?: GridItemProps;
    leftDSGRewardsProgressGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    HMIErrorGridItem?: GridItemProps;
    fullWidthButtonGridItem?: GridItemProps;
    pointsAvailableGridItem?: GridItemProps;
    headingText?: InjectableCss;
    progressSubText?: InjectableCss;
    subHeading?: InjectableCss;
    linkText?: InjectableCss;
    imageWrapper?: InjectableCss;
    dsgLink?: InjectableCss;
    dsgMemberMainHeadingText?: InjectableCss;
    progressPoints?: InjectableCss;
    progressPointsTextWithDate?: InjectableCss;
    pointsWrapper?: InjectableCss;
    regularSubText?: InjectableCss;
}

export const dsgRewardsInformationStyles: DsgRewardsInformationStyles = {
    container: {
        css: css`
            color: ${colorPallete.grey800};
            .boxGrey:empty {
                padding: 0;
                border: 0;
                background: transparent;
            }
            .boxGrey {
                padding: 16px;
                &.accordionMobileOnly {
                    dt {
                        .imageWrapper {
                            display: none;
                        }
                    }
                    dt button {
                        display: flex !important;
                    }
                    .headingGreySectionDesktop {
                        display: none !important;
                    }
                    dd {
                        padding-top: 0;
                        margin-top: 16px;
                        > div {
                            padding-top: 8px;
                            border-top: 1px solid ${colorPallete.grey400};
                            &.withoutPointsAndProgressSection {
                                border-top: none;
                                padding-top: 0;
                                > div {
                                    > div {
                                        padding-top: 0;
                                    }
                                }
                            }
                        }
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 24px;
                                &.accordionMobileOnly {
                                    dd {
                                        .imageWrapper {
                                            display: none;
                                            &.showForDesktop {
                                                display: flex;
                                                img {
                                                    max-height: 48px;
                                                }
                                            }
                                        }
                                    }
                                    dt > button {
                                        cursor: default;
                                        align-items: flex-start;
                                        .imageWrapper {
                                            display: inline-flex;
                                        }
                                        [class*="IconPositioner"] {
                                            display: none;
                                        }
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            position: relative;
                            .imageWrapper {
                            }
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            .boxGrey.accordionMobileOnly dd {
                                padding-bottom: 8px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    dsgPlusGridContainer: {
        gap: 0,
    },
    linkGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding-top: 18px;
            [class*="TypographyStyle"] {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 18px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            align-items: center;
            img {
                width: 48px;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            column-gap: 16px;
                            img {
                                max-height: 64px;
                                width: 64px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    leftDSGRewardsProgressGridItem: {
        width: 12,
        css: css`
            padding-top: 8px;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 16px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fullWidthGridItem: {
        width: 12,
        css: css`
            padding-top: 8px;
            justify-content: space-between;
            align-items: flex-start;
        `,
    },
    HMIErrorGridItem: {
        css: css`
            column-gap: 16px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .iconImageWrapper {
                                position: absolute;
                                top: 24px;
                                right: 24px;
                            }
                            .hmiContent {
                                width: calc(100% - 80px);
                                max-width: 240px;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fullWidthButtonGridItem: {
        width: 12,
        css: css`
            padding-top: 8px;
            justify-content: space-between;
            align-items: flex-end;
            .dsgSecondaryBtn {
                padding: 10px 22px;
                min-height: 48px;
                font: ${buttonFonts.buttonLg.fontWeight} ${buttonFonts.buttonLg.fontSize} /
                    ${buttonFonts.buttonLg.lineHeight} ${buttonFonts.buttonLg.fontFamily};
            }
        `,
    },
    dsgRewardsProgressGridItem: {
        width: 12,
        css: css`
            padding: 16px 0;
            column-gap: 16px;
            align-items: center;
            .progressCircle {
                stroke: ${colorPallete.grey300};
            }
            .progressCircleFilled {
                stroke: ${colorPallete.primaryRed500};
            }
            .progressText {
                font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                    ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                color: ${colorPallete.grey800};
            }
            .progressWrapper {
                position: relative;
                display: flex;
                .progressText {
                    position: absolute;
                    left: 50%;
                    top: 50%;
                    margin: auto;
                    transform: translate(-50%, -50%);
                    white-space: nowrap;
                }
            }
            svg {
                height: 64px;
                width: 64px;
                min-width: 64px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-top: 16px;
                        `,
                    ],
                    "min",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            position: relative;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    dsgRewardsInfoGridItem: {
        width: 12,
    },
    pointsAvailableGridItem: {
        width: 12,
        css: css`
            border-top: 1px solid ${colorPallete.grey400};
            padding-top: 8px;
            flex-direction: column;
            padding-bottom: 16px;
        `,
    },
    regularSubText: {
        css: css`
            &,
            p {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily} !important;
            }
            p {
                margin-bottom: 0;
            }
            &.signUpText {
                margin-bottom: 16px;
                padding-right: 26%;
            }
            @media screen and (min-width: 1280px) {
                &.signUpText {
                    padding-right: 34%;
                }
            }
        `,
    },
    pointsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            column-gap: 16px;
            padding-top: 8px;
            width: 100%;
            img {
                width: 48px;
                height: auto;
                margin-top: -13px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            img {
                                display: none;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headingText: {
        css: css`
            margin-bottom: 0;
            font: ${headlineFonts.headline5SemiBoldSm.fontWeight} ${headlineFonts.headline5SemiBoldSm.fontSize} /
                ${headlineFonts.headline5SemiBoldSm.lineHeight} ${headlineFonts.headline5SemiBoldSm.fontFamily} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-right: 72px;
                            font: ${headlineFonts.headline3SemiBoldLg.fontWeight}
                                ${headlineFonts.headline3SemiBoldLg.fontSize} /
                                ${headlineFonts.headline3SemiBoldLg.lineHeight}
                                ${headlineFonts.headline3SemiBoldLg.fontFamily} !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subHeading: {
        css: css`
            &,
            p {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                    ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily} !important;
                color: ${colorPallete.grey600DisabledText};
                text-transform: uppercase;
                margin-bottom: 0;
            }
            &.signUpText {
                &,
                p {
                    font: ${bodyFonts.body1RegularSm.fontWeight} ${bodyFonts.body1RegularSm.fontSize} /
                        ${bodyFonts.body1RegularSm.lineHeight} ${bodyFonts.body1RegularSm.fontFamily} !important;
                }
            }
        `,
    },
    progressSubText: {
        css: css`
            &,
            p {
                font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                    ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                margin-bottom: 0;
                padding-right: 20px;
            }
            &.fullWidthText {
                &,
                p {
                    padding-right: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &,
                            p {
                                padding-right: 0;
                            }
                        `,
                    ],
                    "minMax",
                )}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            &,
                            p {
                                padding-right: 12%;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dsgMemberMainHeadingText: {
        css: css`
            &,
            p {
                font: ${headlineFonts.headline4SemiBoldSm.fontWeight} ${headlineFonts.headline4SemiBoldSm.fontSize} /
                    ${headlineFonts.headline4SemiBoldSm.lineHeight} ${headlineFonts.headline4SemiBoldSm.fontFamily};
                color: ${colorPallete.grey800};
                margin-bottom: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                            font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                            line-height ${headlineFonts.headline4SemiBoldLg.lineHeight};
                        `,
                        ],
                        "min",
                    )}
            }
            padding-right: 74px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-right: 25%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    progressPoints: {
        css: css`
            font: ${displayHeading.displaySemiboldSm.fontWeight} ${displayHeading.displaySemiboldSm.fontSize} /
                ${displayHeading.displaySemiboldSm.lineHeight} ${displayHeading.displaySemiboldSm.fontFamily};
            margin-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        font-size: ${displayHeading.displaySemiboldLg.fontSize};
                        line-height ${displayHeading.displaySemiboldLg.lineHeight};
                    `,
                    ],
                    "min",
                )}
        `,
    },
    progressPointsTextWithDate: {
        css: css`
            font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize} /
                ${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
            color: ${colorPallete.grey800};
            margin-bottom: 0;
            strong {
                font-weight: ${bodyFonts.body2SemiBoldSm.fontWeight};
            }
        `,
    },
    dsgLink: {
        css: css`
            > [class*="TypographyStyle"] {
                display: flex;
                align-items: center;
                column-gap: 8px;
                font: ${bodyFonts.body2RegularSm.fontWeight} ${buttonFonts.buttonLg.fontSize} /
                    ${buttonFonts.buttonLg.lineHeight} ${buttonFonts.buttonLg.fontFamily};
                strong {
                    font-weight: ${bodyFonts.body2SemiBoldSm.fontWeight};
                }
            }
        `,
    },
};

const styles = dsgRewardsInformationStyles;

const DsgRewardsInformation: React.FC<Props> = ({ session, fields }) => {
    const dsgRewardDetails = session.properties["dsgPlusReward"]
        ? JSON.parse(session.properties["dsgPlusReward"]) ?? null
        : null;
    if (!dsgRewardDetails) {
        return null;
    }

    const { width } = useWindowSize();
    const percentCount = Number(Math.round(dsgRewardDetails.percentToGoal));
    const xRadius = 27.9996;
    const yRadius = 27.9993;
    const svgCX = 31.9993;
    const svgCY = 32.0004;
    const strokeWidth = 8;
    const svgSize = 64;
    const height = Math.pow((xRadius - yRadius) / (xRadius + yRadius), 2);
    const [circumference] = useState(
        Math.PI * (xRadius + yRadius) * (1 + (3 * height) / (10 + Math.sqrt(4 - 3 * height))),
    );

    let offset;
    if (percentCount >= 100) {
        offset = 0;
    } else {
        offset = circumference - (circumference * percentCount) / 100;
    }
    const isDSGMember = dsgRewardDetails?.Properties?.isDSGUser === "true";
    const dsgMemberithHMIError = dsgRewardDetails?.Properties?.isHMIDataAvailable === "false";
    const userBalance = dsgRewardDetails?.Properties?.userBalance;

    const dsgMemberWithoutProgessAndNoPoints =
        (dsgRewardDetails?.Properties?.isDSGUser === "true" &&
        percentCount == 0 &&
        userBalance == 0 &&
        dsgRewardDetails?.Properties?.isHMIDataAvailable === "true");

    const dsgMemberWithProgessAndPoints =
        (dsgRewardDetails?.Properties?.isDSGUser === "true" &&
        (percentCount > 0 && percentCount <= 100 ) &&
        userBalance > 0 &&
        dsgRewardDetails?.Properties?.isHMIDataAvailable === "true");

    const dsgMemberWithProgessAndWithoutPoints =
        (dsgRewardDetails?.Properties?.isDSGUser == "true" &&
        percentCount > 0 &&
        userBalance <= 0 &&
        dsgRewardDetails?.Properties?.isHMIDataAvailable == "true");

    const dsgMemberWithoutProgessAndWithPoints =
        (dsgRewardDetails?.Properties?.isDSGUser === "true" &&
        percentCount <= 0 &&
        userBalance > 0 &&
        dsgRewardDetails?.Properties?.isHMIDataAvailable === "true");

    const dsgMemberWithExceedsProgessAndPoints =
        (dsgRewardDetails?.Properties?.isDSGUser === "true" &&
        percentCount > 100 &&
        userBalance > 0 &&
        dsgRewardDetails?.Properties?.isHMIDataAvailable === "true");

    const dateAsOf = getLocalizedDateTime({
        dateTime: new Date(dsgRewardDetails?.Properties?.lastUpdated),
        language: session.language,
        options: {
            year: "2-digit",
            month: "numeric",
            day: "2-digit",
        },
    });
 
    useEffect(() => {
        const handleTabIndex = () => {
            if (window.innerWidth > 767) {
                const eleList = document.querySelectorAll('.rewardContainer dt button');
                if (eleList.length > 0) {
                    eleList.forEach(ele => {
                        ele.setAttribute('tabindex', '-1');
                    });
                }
            }
        };

        // Delay the execution
        const timeout = setTimeout(handleTabIndex, 2000);

        // Cleanup the timeout if the component unmounts
        return () => clearTimeout(timeout);
    }, [dsgRewardDetails]);   

    return (
        <>
            <StyledWrapper {...styles.container} className="rewardContainer">
                <Accordion headingLevel={2} {...styles} className="accordionMobileOnly boxGrey">
                    {dsgMemberWithProgessAndPoints && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                    <Typography className="imageWrapper">
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer}>
                                {fields.dsgProgressToGoalTitle && (
                                    <GridItem {...styles.dsgRewardsInfoGridItem}>
                                        <StyledWrapper {...styles.subHeading}>
                                            {parse(fields.dsgProgressToGoalTitle, parserOptions)}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...styles.dsgRewardsProgressGridItem} className="dsgRewardsProgressGridItem">
                                    <StyledWrapper className="progressWrapper">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            width={svgSize}
                                            height={svgSize}
                                            viewBox={`0 0 ${svgSize} ${svgSize}`}
                                        >
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Ellipse 432"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(-90 ${svgCX} ${svgCY})`}
                                                className="progressCircle"
                                            />
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                stroke-linecap="round"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(90 ${svgCX} ${svgCY})`}
                                                id="Ellipse 433"
                                                className="progressCircleFilled"
                                                style={{
                                                    strokeDasharray: `${circumference}`,
                                                    strokeDashoffset: percentCount > 2 ? offset + 2 : offset,
                                                }}
                                            />
                                        </svg>
                                        <Typography as="h6" className="progressText">
                                            {percentCount}%
                                        </Typography>
                                    </StyledWrapper>

                                    <StyledWrapper>
                                        {fields.dsgGoalBetweenRangeMessage && (
                                            <StyledWrapper {...styles.progressSubText}  className="dsgProgressBetweenRange">
                                                {parse(fields.dsgGoalBetweenRangeMessage, parserOptions)}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.pointsAvailableGridItem} className="pointsAvailableGridItem">
                                    <Typography {...styles.subHeading}>
                                        {translate("Points available as of")} {dateAsOf}
                                    </Typography>
                                    <StyledWrapper {...styles.pointsWrapper}>
                                        <Typography {...styles.progressPoints}>
                                            {userBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </Typography>
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.linkGridItem} className="dsgLinkGridItem">
                                    {fields.viewDsgPlusSiteLinkText && (
                                        <Link
                                            href={useGetLink(fields.viewDsgPlusSiteLinkUrl).url}
                                            className="text-link text-link-secondary"
                                            {...styles.dsgLink}
                                            target={fields.openViewLinkInNewWindow ? "_blank" : ""}
                                        >
                                            <Typography>
                                                {parse(fields.viewDsgPlusSiteLinkText, parserOptions)}
                                            </Typography>
                                            <OpenInNewWindow />
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                    {dsgMemberWithProgessAndWithoutPoints && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                    <Typography className="imageWrapper">
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''}/>}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer}>
                                {fields.dsgProgressToGoalTitle && (
                                    <GridItem {...styles.dsgRewardsInfoGridItem}>
                                        <StyledWrapper {...styles.subHeading}>
                                            {parse(fields.dsgProgressToGoalTitle, parserOptions)}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...styles.dsgRewardsProgressGridItem} className="dsgRewardsProgressGridItem">
                                    <StyledWrapper className="progressWrapper">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            width={svgSize}
                                            height={svgSize}
                                            viewBox={`0 0 ${svgSize} ${svgSize}`}
                                        >
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Ellipse 432"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(-90 ${svgCX} ${svgCY})`}
                                                className="progressCircle"
                                            />
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                stroke-linecap="round"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(90 ${svgCX} ${svgCY})`}
                                                id="Ellipse 433"
                                                className="progressCircleFilled"
                                                style={{
                                                    strokeDasharray: `${circumference}`,
                                                    strokeDashoffset: percentCount > 2 ? offset + 2 : offset,
                                                }}
                                            />
                                        </svg>
                                        <Typography as="h6" className="progressText">
                                            {percentCount}%
                                        </Typography>
                                    </StyledWrapper>

                                    <StyledWrapper>
                                        {percentCount > 0 && percentCount <= 100 && (
                                            <>
                                                {fields.dsgGoalBetweenRangeMessage && (
                                                    <StyledWrapper {...styles.progressSubText}  className="dsgProgressBetweenRange">
                                                        {parse(fields.dsgGoalBetweenRangeMessage, parserOptions)}
                                                    </StyledWrapper>
                                                )}
                                            </>
                                        )}
                                        {percentCount > 100 && (
                                            <>
                                                {fields.dsgGoalExceedsMessage && (
                                                    <StyledWrapper
                                                        {...styles.progressSubText}
                                                        className="fullWidthText"
                                                    >
                                                        {parse(fields.dsgGoalExceedsMessage, parserOptions)}
                                                    </StyledWrapper>
                                                )}
                                            </>
                                        )}
                                    </StyledWrapper>
                                </GridItem>

                                <GridItem {...styles.linkGridItem} className="dsgLinkGridItem">
                                    {fields.viewDsgPlusSiteLinkText && (
                                        <Link
                                            href={useGetLink(fields.viewDsgPlusSiteLinkUrl).url}
                                            className="text-link text-link-secondary"
                                            {...styles.dsgLink}
                                            target={fields.openViewLinkInNewWindow ? "_blank" : ""}
                                        >
                                            <Typography>
                                                {parse(fields.viewDsgPlusSiteLinkText, parserOptions)}
                                            </Typography>
                                            <OpenInNewWindow />
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                    {dsgMemberWithoutProgessAndWithPoints && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                    <Typography className="imageWrapper">
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer}>
                                {fields.dsgProgressToGoalTitle && (
                                    <GridItem {...styles.dsgRewardsInfoGridItem}>
                                        <StyledWrapper {...styles.subHeading}>
                                            {parse(fields.dsgProgressToGoalTitle, parserOptions)}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...styles.dsgRewardsProgressGridItem} className="dsgRewardsProgressGridItem">
                                    <StyledWrapper className="progressWrapper">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            width={svgSize}
                                            height={svgSize}
                                            viewBox={`0 0 ${svgSize} ${svgSize}`}
                                        >
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Ellipse 432"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(-90 ${svgCX} ${svgCY})`}
                                                className="progressCircle"
                                            />
                                        </svg>
                                        <Typography as="h6" className="progressText">
                                            0%
                                        </Typography>
                                    </StyledWrapper>

                                    <StyledWrapper>
                                        {fields.dsgZeroGoalMessage && (
                                            <StyledWrapper {...styles.progressSubText} className="fullWidthText">
                                                {parse(fields.dsgZeroGoalMessage, parserOptions)}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.pointsAvailableGridItem} className="pointsAvailableGridItem">
                                    <Typography {...styles.subHeading}>
                                        {translate("Points available as of")} {dateAsOf}
                                    </Typography>
                                    <StyledWrapper {...styles.pointsWrapper}>
                                        <Typography {...styles.progressPoints}>
                                            {userBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </Typography>
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.linkGridItem} className="dsgLinkGridItem">
                                    {fields.viewDsgPlusSiteLinkText && (
                                        <Link
                                            href={useGetLink(fields.viewDsgPlusSiteLinkUrl).url}
                                            className="text-link text-link-secondary"
                                            {...styles.dsgLink}
                                            target={fields.openViewLinkInNewWindow ? "_blank" : ""}
                                        >
                                            <Typography>
                                                {parse(fields.viewDsgPlusSiteLinkText, parserOptions)}
                                            </Typography>
                                            <OpenInNewWindow />
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                    {dsgMemberWithExceedsProgessAndPoints && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                    <Typography className="imageWrapper">
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer}>
                                {fields.dsgProgressToGoalTitle && (
                                    <GridItem {...styles.dsgRewardsInfoGridItem}>
                                        <StyledWrapper {...styles.subHeading}>
                                            {parse(fields.dsgProgressToGoalTitle, parserOptions)}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...styles.dsgRewardsProgressGridItem} className="dsgRewardsProgressGridItem">
                                    <StyledWrapper className="progressWrapper">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            width={svgSize}
                                            height={svgSize}
                                            viewBox={`0 0 ${svgSize} ${svgSize}`}
                                        >
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Ellipse 432"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(-90 ${svgCX} ${svgCY})`}
                                                className="progressCircle"
                                            />
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                stroke-linecap="round"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(90 ${svgCX} ${svgCY})`}
                                                id="Ellipse 433"
                                                className="progressCircleFilled"
                                                style={{
                                                    strokeDasharray: `${circumference}`,
                                                    strokeDashoffset: percentCount > 2 ? offset + 2 : offset,
                                                }}
                                            />
                                        </svg>
                                        <Typography as="h6" className="progressText">
                                            {percentCount}%
                                        </Typography>
                                    </StyledWrapper>

                                    <StyledWrapper>
                                        {fields.dsgGoalExceedsMessage && (
                                            <StyledWrapper {...styles.progressSubText} className="fullWidthText">
                                                {parse(fields.dsgGoalExceedsMessage, parserOptions)}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.pointsAvailableGridItem} className="pointsAvailableGridItem">
                                    <Typography {...styles.subHeading}>
                                        {translate("Points available as of")} {dateAsOf}
                                    </Typography>
                                    <StyledWrapper {...styles.pointsWrapper}>
                                        <Typography {...styles.progressPoints}>
                                            {userBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </Typography>
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.linkGridItem} className="dsgLinkGridItem">
                                    {fields.viewDsgPlusSiteLinkText && (
                                        <Link
                                            href={useGetLink(fields.viewDsgPlusSiteLinkUrl).url}
                                            className="text-link text-link-secondary"
                                            {...styles.dsgLink}
                                            target={fields.openViewLinkInNewWindow ? "_blank" : ""}
                                        >
                                            <Typography>
                                                {parse(fields.viewDsgPlusSiteLinkText, parserOptions)}
                                            </Typography>
                                            <OpenInNewWindow />
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                    {dsgMemberWithoutProgessAndNoPoints && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                    <Typography className="imageWrapper">
                                        {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer}>
                                {fields.dsgProgressToGoalTitle && (
                                    <GridItem {...styles.dsgRewardsInfoGridItem}>
                                        <StyledWrapper {...styles.subHeading}>
                                            {parse(fields.dsgProgressToGoalTitle, parserOptions)}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...styles.dsgRewardsProgressGridItem}  className="dsgRewardsProgressGridItem">
                                    <StyledWrapper className="progressWrapper">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            width={svgSize}
                                            height={svgSize}
                                            viewBox={`0 0 ${svgSize} ${svgSize}`}
                                        >
                                            <ellipse
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Ellipse 432"
                                                cx={svgCX}
                                                cy={svgCY}
                                                rx={xRadius}
                                                ry={yRadius}
                                                stroke-width={strokeWidth}
                                                transform={`rotate(-90 ${svgCX} ${svgCY})`}
                                                className="progressCircle"
                                            />
                                        </svg>
                                        <Typography as="h6" className="progressText">
                                            0%
                                        </Typography>
                                    </StyledWrapper>

                                    <StyledWrapper>
                                        {fields.dsgZeroGoalMessage && (
                                            <StyledWrapper {...styles.progressSubText} className="fullWidthText">
                                                {parse(fields.dsgZeroGoalMessage, parserOptions)}
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem {...styles.linkGridItem} className="dsgLinkGridItem">
                                    {fields.viewDsgPlusSiteLinkText && (
                                        <Link
                                            href={useGetLink(fields.viewDsgPlusSiteLinkUrl).url}
                                            className="text-link text-link-secondary"
                                            {...styles.dsgLink}
                                            target={fields.openViewLinkInNewWindow ? "_blank" : ""}
                                        >
                                            <Typography>
                                                {parse(fields.viewDsgPlusSiteLinkText, parserOptions)}
                                            </Typography>
                                            <OpenInNewWindow />
                                        </Link>
                                    )}
                                </GridItem>
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}

                    {!isDSGMember && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <>
                                    <Typography {...styles.headingText}>
                                        {parse(fields.dsgPlusTitle, parserOptions)}
                                    </Typography>
                                </>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer} className="withoutPointsAndProgressSection">
                                <GridItem {...styles.fullWidthGridItem}>
                                    {fields.dsgSignUpMessage && (
                                        <StyledWrapper {...styles.regularSubText} className="signUpText">
                                            {parse(fields.dsgSignUpMessage, parserOptions)}
                                        </StyledWrapper>
                                    )}
                                </GridItem>

                                {fields.signUpDsgLinkText && (
                                    <GridItem {...styles.fullWidthButtonGridItem}>
                                        <Link
                                            href={useGetLink(fields.signUpDsgLinkUrl).url}
                                            className="dsgBtn dsgSecondaryBtn"
                                            target={fields.openSignUpLinkInNewWindow ? "_blank" : ""}
                                        >
                                            {parse(fields.signUpDsgLinkText, parserOptions)}
                                        </Link>
                                        <StyledWrapper {...styles.imageWrapper} className="imageWrapper showForDesktop">
                                            {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                    {dsgMemberithHMIError && (
                        <ManagedAccordionSection
                            initialExpanded={false}
                            title={
                                <Typography {...styles.headingText}>
                                    {parse(fields.dsgPlusTitle, parserOptions)}
                                </Typography>
                            }
                        >
                            <GridContainer {...styles.dsgPlusGridContainer} className="withoutPointsAndProgressSection">
                                {fields.dsgHMIErrorMessage && (
                                    <GridItem {...styles.fullWidthGridItem} {...styles.HMIErrorGridItem}>
                                        <StyledWrapper {...styles.regularSubText} className="hmiContent">
                                            {parse(fields.dsgHMIErrorMessage, parserOptions)}
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.imageWrapper} className="iconImageWrapper">
                                            {fields.dsgPlusIcon && <Img src={fields.dsgPlusIcon} altText={fields.dsgPlusIconAltText || ''} />}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                </Accordion>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(DsgRewardsInformation)),
    definition: {
        group: "Common",
        displayName: "DSG Rewards Information on Cart, Order Review and Confirmation",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.dsgPlusTitle,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "DSG Plus",
            },
            {
                name: fields.dsgPlusIcon,
                displayName: "Dsg Plus Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.dsgPlusIconAltText,
                displayName: "Dsg Plus Icon Alt Text",
                editorTemplate: "TextField",
                defaultValue: "DSG Plus Reward Icon",
                fieldType: "Translatable",
            },
            {
                fieldType: "Contextual",
                name: fields.dsgProgressToGoalTitle,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "Progress to next goal",
            },
            {
                fieldType: "Contextual",
                name: fields.dsgGoalBetweenRangeMessage,
                editorTemplate: "RichTextField",
                displayName: "Goal Between Range 0 to 100 Message Text",
                defaultValue: "<p>Once you reach your goal, you’ll earn points!</p>",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                fieldType: "Contextual",
                name: fields.dsgZeroGoalMessage,
                editorTemplate: "RichTextField",
                displayName: "Zero Goal Message Text",
                defaultValue: "<p>Get started earning points today!</p>",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                fieldType: "Contextual",
                name: fields.dsgGoalExceedsMessage,
                editorTemplate: "RichTextField",
                displayName: "Goal Exceeds Over 100% Message Text",
                defaultValue: "<p>Congratulations! You met your annual goal. You're now earning even more!</p>",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                fieldType: "Contextual",
                name: fields.dsgHMIErrorMessage,
                editorTemplate: "RichTextField",
                displayName: "HMI Error Message Text",
                defaultValue:
                    "<p>Sorry! We’re having a momentary issue getting your progress. But, good news! If you choose to purchase today, you will still earn points toward your annual goal.</p>",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                fieldType: "Contextual",
                name: fields.dsgSignUpMessage,
                editorTemplate: "RichTextField",
                displayName: "Sign Up Message Text",
                defaultValue: "<p>Start earning toward rewards. Sign up with DSG Plus today!</p>",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                fieldType: "General",
                name: fields.viewDsgPlusSiteLinkText,
                displayName: "View DSG Plus Site Link Text",
                editorTemplate: "TextField",
                defaultValue: "<strong>View DSG Plus site</strong> for specific details",
            },
            {
                fieldType: "Contextual",
                name: fields.viewDsgPlusSiteLinkUrl,
                displayName: "View DSG Plus Site Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openViewLinkInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.signUpDsgLinkText,
                displayName: "Sign Up Link Text",
                editorTemplate: "TextField",
                defaultValue: "Sign up",
            },
            {
                fieldType: "Contextual",
                name: fields.signUpDsgLinkUrl,
                displayName: "Sign Up Link Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openSignUpLinkInNewWindow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
